import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, Clock, Eye,
  CheckCircle, XCircle, Timer, Building, User, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import invitationService from '../services/invitationService';
import configService from '../services/configService';
import { PageLoader } from '../components/CommonLoader';
import { useAuth } from '../contexts/AuthContext';
import EnhancedDataTable from '../components/EnhancedDataTable';

const UserInvitations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingInvite, setProcessingInvite] = useState(null);
  const [showNdaModal, setShowNdaModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [ndaText, setNdaText] = useState('');
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [loadingNda, setLoadingNda] = useState(false);

  useEffect(() => {
    fetchPendingInvitations();
  }, []);

  const fetchPendingInvitations = async () => {
    try {
      setLoading(true);
      const response = await invitationService.getPending();
      
      // Handle response structure - directly use the data
      const invitationsList = response?.data || response?.invitations || response || [];
      setInvitations(Array.isArray(invitationsList) ? invitationsList : []);
    } catch (error) {
      toast.error('Failed to load invitations');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvitation = (invitation) => {
    navigate(`/invitation/${invitation.token}`);
  };

  const fetchNdaForInvitation = async (invitation) => {
    try {
      setLoadingNda(true);
      const ndaResponse = await configService.getPublicConfig('NDA_TEXT');
      const ndaContent = ndaResponse?.data?.value || ndaResponse?.value || '';
      setNdaText(ndaContent);
      return ndaContent;
    } catch (error) {
      setNdaText('');
      return '';
    } finally {
      setLoadingNda(false);
    }
  };

  const handleAcceptClick = async (invitation) => {
    setSelectedInvitation(invitation);
    
    // Check if NDA is required
    const nda = await fetchNdaForInvitation(invitation);
    if (nda) {
      setShowNdaModal(true);
      setNdaAccepted(false);
    } else {
      // No NDA required, accept directly
      handleAccept(invitation);
    }
  };

  const handleAccept = async (invitation) => {
    if (ndaText && !ndaAccepted) {
      toast.error('Please accept the NDA before accepting the invitation');
      return;
    }
    
    try {
      setProcessingInvite(invitation.id);
      const response = await invitationService.accept(invitation.token);
      toast.success(response.message || 'Invitation accepted successfully');
      
      // Close modal if open
      setShowNdaModal(false);
      setNdaAccepted(false);
      setSelectedInvitation(null);
      
      // Refresh the list
      fetchPendingInvitations();
      
      // Redirect to dataroom if UUID is provided
      if (response.dataroom_uuid) {
        navigate(`/datarooms/${response.dataroom_uuid}/files`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleDecline = async (invitation) => {
    if (!window.confirm('Are you sure you want to decline this invitation?')) {
      return;
    }
    
    try {
      setProcessingInvite(invitation.id);
      const response = await invitationService.decline(invitation.token);
      toast.success(response.message || 'Invitation declined');
      fetchPendingInvitations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to decline invitation');
    } finally {
      setProcessingInvite(null);
    }
  };

  const getStatusBadge = (statusText) => {
    const statusLabel = statusText || 'Unknown';
    const lowerStatus = statusLabel.toLowerCase();
    
    let color = 'bg-gray-100 text-gray-800';
    
    if (lowerStatus.includes('pending')) {
      color = 'bg-yellow-100 text-yellow-800';
    } else if (lowerStatus.includes('accepted') || lowerStatus.includes('active')) {
      color = 'bg-green-100 text-green-800';
    } else if (lowerStatus.includes('declined') || lowerStatus.includes('rejected')) {
      color = 'bg-red-100 text-red-800';
    } else if (lowerStatus.includes('expired')) {
      color = 'bg-gray-100 text-gray-800';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {statusLabel}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if invitation is expired
  const isExpired = (invitation) => {
    if (!invitation.expires_at) return false;
    return new Date(invitation.expires_at) < new Date();
  };

  // Define actions column with buttons instead of icons
  const actionsColumn = {
    key: 'actions',
    header: 'Actions',
    sortable: false,
    render: (value, row) => {
      const isPending = row.status === 0 || row.status_text?.toLowerCase() === 'pending';
      const expired = isExpired(row);
      
      return (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewInvitation(row);
            }}
            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary-500"
            title="View Details"
          >
            <Eye className="w-3.5 h-3.5 mr-1" />
            View
          </button>
          
          {isPending && !expired && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAcceptClick(row);
                }}
                disabled={processingInvite === row.id}
                className="inline-flex items-center px-2.5 py-1.5 border border-green-300 shadow-sm text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Accept Invitation"
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Accept
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDecline(row);
                }}
                disabled={processingInvite === row.id}
                className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Decline Invitation"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Decline
              </button>
            </>
          )}
        </div>
      );
    }
  };

  // Define table columns matching Subscription list design
  const columns = [
    {
      key: 'dataroom',
      header: 'Dataroom',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center">
          <Building className="h-5 w-5 text-gray-400 mr-2" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {row.dataroom?.name || 'Unknown Dataroom'}
            </div>
            <div className="text-xs text-gray-500">
              {row.dataroom?.company_name || 'Company N/A'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'invited_by',
      header: 'Invited By',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center">
          <User className="h-4 w-4 text-gray-400 mr-2" />
          <div>
            <div className="text-sm text-gray-900">
              {row.invited_by ? 
                `${row.invited_by.first_name || ''} ${row.invited_by.last_name || ''}`.trim() || 
                row.invited_by.email : 
                'Unknown'}
            </div>
            {row.invited_by?.email && (
              <div className="text-xs text-gray-500">
                {row.invited_by.email}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: false,
      render: (value, row) => getStatusBadge(row.status_text || row.status)
    },
    {
      key: 'created_at',
      header: 'Created On',
      sortable: false,
      render: (value, row) => (
        <div className="text-sm text-gray-500">
          <div className="flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            {formatDate(row.created_at)}
          </div>
        </div>
      )
    },
    {
      key: 'expires_at',
      header: 'Expires On',
      sortable: false,
      render: (value, row) => (
        <div className="text-sm">
          {row.expires_at ? (
            <div className={`flex items-center ${isExpired(row) ? 'text-red-600' : 'text-gray-500'}`}>
              <Clock className="h-3 w-3 mr-1" />
              {isExpired(row) ? 'Expired' : formatDate(row.expires_at)}
            </div>
          ) : (
            <span className="text-gray-400">No expiry</span>
          )}
        </div>
      )
    },
    actionsColumn // Add the actions column
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Invitations</h1>
              <p className="text-sm text-gray-600">Manage your dataroom invitations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Invitations Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {invitations.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invitations</h3>
            <p className="text-gray-600">You don't have any dataroom invitations at the moment.</p>
          </div>
        ) : (
          <EnhancedDataTable
            data={invitations}
            columns={columns}
            loading={loading}
            emptyMessage="No invitations found"
            showPagination={false}
          />
        )}
      </div>

      {/* NDA Modal */}
      {showNdaModal && selectedInvitation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Non-Disclosure Agreement
              </h3>
              
              {loadingNda ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : ndaText ? (
                <div>
                  <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 max-h-96 overflow-y-auto mb-4">
                    <div 
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: ndaText }}
                    />
                  </div>
                  
                  <div className="flex items-start gap-3 mb-6">
                    <input
                      type="checkbox"
                      id="nda-accept"
                      checked={ndaAccepted}
                      onChange={(e) => setNdaAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                    />
                    <label htmlFor="nda-accept" className="text-sm text-gray-700 cursor-pointer">
                      I have read and agree to the terms of the Non-Disclosure Agreement
                    </label>
                  </div>
                </div>
              ) : (
                <p className="text-gray-600 mb-6">No NDA text available</p>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowNdaModal(false);
                    setNdaAccepted(false);
                    setSelectedInvitation(null);
                  }}
                  className="px-3 py-1.5 text-sm bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAccept(selectedInvitation)}
                  disabled={ndaText && !ndaAccepted}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Accept Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInvitations;